<template>
  <div
    v-if="trialPurchaseReceipt"
    class="trial-banner-component"
    :class="{expired: expired}"
  >
    <p
      v-if="expired"
      class="trial-text"
    >
      Your free access has expired.
    </p>
    <template
      v-else
    >
      <p
        class="trial-text full"
      >
        <span class="time-left">{{ timeLeftLabel }}</span> remain of full, free access.
      </p>
      <p class="trial-text abbreviated">
        <span class="time-left">{{ abbreviatedTimeLeftLabel }}</span> remain of full, free access.
      </p>
    </template>
    <button
      class="action-btn"
      @click="bannerClicked"
    >
      Be a member
    </button>
  </div>
</template>

<script>
import { differenceInDays, differenceInSeconds } from 'date-fns';

import GlobalMixin from '@/mixins/global';
import TrialMixin from '@/mixins/trial';

export default {
  mixins: [GlobalMixin, TrialMixin],
  data() {
    return {
      timeLeftInSeconds: null,
      timerExpired: false,
      updateInterval: null,
    };
  },
  computed: {
    abbreviatedTimeLeftLabel() {
      return this.timeLeftLabel.replace('hours', 'hr').replace('minutes', 'min').replace('seconds', 'sec');
    },
    expired() {
      // This computed is used for cases where the trial banner countdown expires while the user is on the page
      // It won't take into account the pvar in that instance, however. This is due to the app-banner component
      // controlling the app layout to show any type of banner. We would need a way to bubble up the timer expiration
      // and a way to let the app banner component know to rerun the logic for determining whether or not to be displayed.
      // Since this is a rare edge case, we've decided it was an acceptable solution to avoid additional complexity in the
      // app banner.
      return this.trialPurchaseReceiptExpired || this.timerExpired;
    },
    lessThan24HoursLeft() {
      if (!this.trialPurchaseReceiptExpired) {
        const secondsIn24Hours = 60 * 60 * 24;
        return this.timeLeftInSeconds < secondsIn24Hours;
      }
    },
    timeLeftLabel() {
      if (this.lessThan24HoursLeft && this.timeLeftInSeconds && !isNaN(this.timeLeftInSeconds)) {
        const hours = Math.floor(this.timeLeftInSeconds / 60 / 60);
        const minutes = Math.floor((this.timeLeftInSeconds - (hours * 60 * 60)) / 60);
        const seconds = Math.floor(this.timeLeftInSeconds - (hours * 60 * 60) - (minutes * 60));
        return `${hours} hours, ${minutes} minutes, ${seconds} seconds`;
      }
      // We want to round up when this mesage is display. differenceInDays will round down so we'll compensate by adding 1 to it
      return `${differenceInDays(this.trialPurchaseReceiptExpiration, new Date()) + 1} days`;
    },
  },
  mounted() {
    if (this.trialPurchaseReceipt) {
      this.runTimeUpdate();
      if (this.lessThan24HoursLeft) {
        this.updateInterval = setInterval(this.runTimeUpdate, 1000);
      }
    }
  },
  methods: {
    bannerClicked() {
      this.$analytics.events.selectedTrialBanner(this.currentResource, this.trialPurchaseReceiptExpiration, this.expired);
      this.$store.dispatch('ui/toggleUpsellModal', { show: true, source: 'Trial Banner'});
    },
    runTimeUpdate() {
      this.timeLeftInSeconds = differenceInSeconds(this.trialPurchaseReceiptExpiration, new Date());
      this.timerExpired = this.timeLeftInSeconds <= 0;
      if (this.expired) {
        clearInterval(this.updateInterval);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.trial-banner-component {
  background-color: #001c74;
  color: white;
  text-align: center;
  font-size: rem-calc(16);
  margin: 0;
  height: 100%;
  width: 100%;

  .trial-text {
    margin: rem-calc(18) 0 0; // Margin = total height (58) - font-size (16) / 2 (top & bottom spacing) and adjusted slightly
    display: inline-block;

    .time-left {
      font-weight: $semi-bold;
    }

    &.abbreviated {
      display: none;
    }

    @include breakpoint(small) {
      &.full {
        display: none;
      }

      &.abbreviated {
        display: inline-block;
      }
    }
  }

  .action-btn {
    $accent-color: #ffd83e;
    background-color: transparent;
    text-transform: uppercase;
    color: $accent-color;
    padding: 0;
    font-weight: $semi-bold;
    font-size: rem-calc(14);
    letter-spacing: -.25px;
    display: inline-block;
    margin-left: rem-calc(4);
    border-bottom: 1px solid $accent-color;
  }

  &.expired {
    background-color: #dc3416;

    .action-btn {
      $accent-color: #ffffff;
      color: $accent-color;
      border-bottom: 1px solid $accent-color;
    }
  }

  @include breakpoint(small) {
    .action-btn {
      margin: rem-calc(4) auto 0;
      display: block;
    }
  }
}
</style>